import { MASTER_ITEMS } from './mockPrStore';
import { syncUomToDatabase } from './dbBridge';

export interface UomRecord {
  id: string;
  code: string;
  name: string;
  symbol: string;
  description: string;
  category: string; // e.g. 'Count', 'Length', 'Weight', 'Packaging'
  createdAt: string;
}

export const uomDatabase: UomRecord[] = [
  {
    id: 'uom-001',
    code: 'PCS',
    name: 'Pieces / Buah',
    symbol: 'pcs',
    description: 'Satuan hitung unit terpisah untuk barang jadi, kancing, dan pallet kayu.',
    category: 'Count',
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'uom-002',
    code: 'BOX',
    name: 'Box / Karton',
    symbol: 'bx',
    description: 'Satuan kemasan kardus karton luar untuk pengiriman dan penyimpanan.',
    category: 'Packaging',
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'uom-003',
    code: 'KG',
    name: 'Kilogram',
    symbol: 'kg',
    description: 'Satuan massa berat standar metrik untuk bahan kimia, pewarna tekstil, dan benang.',
    category: 'Weight',
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'uom-004',
    code: 'MTR',
    name: 'Meter',
    symbol: 'm',
    description: 'Satuan panjang standar tekstil untuk kain gulungan, pita, dan webbing.',
    category: 'Length',
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'uom-005',
    code: 'ROLL',
    name: 'Roll / Gulungan',
    symbol: 'rl',
    description: 'Satuan gulungan untuk bubble wrap, stretch film, lakban, dan ribbon printer barcode.',
    category: 'Packaging',
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'uom-006',
    code: 'YARD',
    name: 'Yard',
    symbol: 'yd',
    description: 'Satuan panjang tekstil impor standar internasional (1 yard = 0.9144 meter).',
    category: 'Length',
    createdAt: '2026-02-10T00:00:00.000Z',
  },
];

let uomSeq = 6;

export function getAllUoms(search?: string): UomRecord[] {
  let list = [...uomDatabase];
  if (search) {
    const q = search.toLowerCase().trim();
    list = list.filter(
      (u) =>
        u.code.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q) ||
        u.symbol.toLowerCase().includes(q) ||
        u.description.toLowerCase().includes(q)
    );
  }
  return list.sort((a, b) => a.code.localeCompare(b.code));
}

export function getUomById(id: string): (UomRecord & { linkedItems: typeof MASTER_ITEMS }) | undefined {
  const q = id.toLowerCase().trim();
  const uom = uomDatabase.find((u) => u.id.toLowerCase() === q || u.code.toLowerCase() === q);
  if (!uom) return undefined;

  const linkedItems = MASTER_ITEMS.filter((m) => m.uomCode.toUpperCase() === uom.code.toUpperCase());
  return {
    ...uom,
    linkedItems,
  };
}

export function createUom(data: {
  code: string;
  name: string;
  symbol: string;
  description?: string;
  category?: string;
}): UomRecord {
  const code = data.code.trim().toUpperCase();

  if (uomDatabase.some((u) => u.code.toUpperCase() === code)) {
    throw new Error(`Satuan dengan kode '${code}' sudah terdaftar.`);
  }

  uomSeq += 1;
  const newUom: UomRecord = {
    id: `uom-${String(uomSeq).padStart(3, '0')}`,
    code,
    name: data.name.trim(),
    symbol: data.symbol.trim(),
    description: data.description?.trim() || 'Satuan pengukuran operasional.',
    category: data.category || 'General',
    createdAt: new Date().toISOString(),
  };

  uomDatabase.push(newUom);
  syncUomToDatabase(newUom);
  return newUom;
}

export function updateUom(
  id: string,
  data: Partial<Omit<UomRecord, 'id' | 'createdAt'>>
): UomRecord {
  const uom = uomDatabase.find((u) => u.id === id || u.code === id);
  if (!uom) {
    throw new Error(`Satuan UoM '${id}' tidak ditemukan.`);
  }

  if (data.code && data.code.trim().toUpperCase() !== uom.code.toUpperCase()) {
    const newCode = data.code.trim().toUpperCase();
    if (uomDatabase.some((u) => u.id !== uom.id && u.code.toUpperCase() === newCode)) {
      throw new Error(`Kode satuan '${newCode}' sudah digunakan.`);
    }
    uom.code = newCode;
  }

  if (data.name) uom.name = data.name.trim();
  if (data.symbol) uom.symbol = data.symbol.trim();
  if (data.description !== undefined) uom.description = data.description.trim();
  if (data.category) uom.category = data.category;
  syncUomToDatabase(uom);

  return uom;
}

export function deleteUom(id: string): { success: boolean; message: string } {
  const index = uomDatabase.findIndex((u) => u.id === id || u.code === id);
  if (index === -1) {
    throw new Error(`Satuan UoM '${id}' tidak ditemukan.`);
  }

  const uom = uomDatabase[index];

  // Check if UoM is used by catalog items
  const linkedItems = MASTER_ITEMS.filter((m) => m.uomCode.toUpperCase() === uom.code.toUpperCase());
  if (linkedItems.length > 0) {
    throw new Error(
      `Satuan '${uom.code}' tidak dapat dihapus karena sedang digunakan oleh ${linkedItems.length} item master katalog (${linkedItems.map((m) => m.code).join(', ')}).`
    );
  }

  uomDatabase.splice(index, 1);
  return {
    success: true,
    message: `Satuan '${uom.code} - ${uom.name}' berhasil dihapus.`,
  };
}
